import { useCallback, useMemo, useSyncExternalStore } from "react";

export interface CartItem {
  readonly name: string;
  readonly sku: string;
  readonly sizeLabel: string | null;
}

export interface CartLine {
  readonly variantId: string;
  readonly qty: number;
  readonly item: CartItem | null;
}

const listeners = new Set<() => void>();

function storageKey(franchiseId: string): string {
  return `colorglo.cart.${franchiseId}`;
}

function read(franchiseId: string): string {
  try {
    return localStorage.getItem(storageKey(franchiseId)) ?? "[]";
  } catch {
    return "[]";
  }
}

function parse(raw: string): CartLine[] {
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.flatMap((entry: unknown) => {
      const line = entry as Partial<CartLine>;
      const item = line.item as Partial<CartItem> | null | undefined;
      return typeof line.variantId === "string" &&
        typeof line.qty === "number" &&
        Number.isInteger(line.qty) &&
        line.qty > 0
        ? [
            {
              variantId: line.variantId,
              qty: line.qty,
              item:
                typeof item?.name === "string" && typeof item.sku === "string"
                  ? { name: item.name, sku: item.sku, sizeLabel: item.sizeLabel ?? null }
                  : null,
            },
          ]
        : [];
    });
  } catch {
    return [];
  }
}

function write(franchiseId: string, lines: readonly CartLine[]): void {
  try {
    localStorage.setItem(storageKey(franchiseId), JSON.stringify(lines));
  } catch {
    return;
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

export const MAX_QTY = 9999;

export function useCart(franchiseId: string | null) {
  const id = franchiseId ?? "";
  const raw = useSyncExternalStore(subscribe, () => (id === "" ? "[]" : read(id)));
  const lines = useMemo(() => parse(raw), [raw]);

  const setQty = useCallback(
    (variantId: string, qty: number, item: CartItem | null = null) => {
      if (id === "") return;
      const current = parse(read(id));
      const clamped = Math.min(Math.max(Math.trunc(qty), 0), MAX_QTY);
      const existing = current.find((line) => line.variantId === variantId);
      const next =
        clamped === 0
          ? current.filter((line) => line.variantId !== variantId)
          : existing === undefined
            ? [...current, { variantId, qty: clamped, item }]
            : current.map((line) =>
                line.variantId === variantId
                  ? { variantId, qty: clamped, item: item ?? line.item }
                  : line,
              );
      write(id, next);
    },
    [id],
  );

  const add = useCallback(
    (variantId: string, item: CartItem) => {
      const existing = parse(read(id)).find((line) => line.variantId === variantId)?.qty ?? 0;
      setQty(variantId, existing + 1, item);
    },
    [id, setQty],
  );

  const clear = useCallback(() => {
    if (id !== "") write(id, []);
  }, [id]);

  return {
    lines,
    count: lines.reduce((sum, line) => sum + line.qty, 0),
    add,
    setQty,
    remove: (variantId: string) => setQty(variantId, 0),
    clear,
  };
}
